import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { PortalSearchQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { useMemo, useState } from "react";
import { CreateEditPortalDialog } from "./CreateEditDialog";
import { PORTAL_SEARCH } from "./api";

type Row = PortalSearchQuery["portalSearch"]["items"][number];

type PortalsProps = {
  initialPageSize?: number;
};

export const Portals: React.FC<PortalsProps> = ({ initialPageSize }) => {
  const {
    selectedId: editPortalId,
    pushIdToUrl,
    popIdFromUrl,
  } = useSelectedIdInUrl("portalId");

  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Portals",
    },
  ]);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "description", headerName: "Description", flex: 2 },
      { field: "type", headerName: "Type", width: 100 },
      { field: "jurisdiction", headerName: "Jurisdiction", flex: 1 },
      {
        field: "portalTech",
        headerName: "Portal Tech",
        flex: 1,
        valueGetter: (_, row) => row.portalTech ?? "-",
      },
      { field: "url", headerName: "URL", flex: 2 },
      { field: "isActive", headerName: "Active", width: 80, type: "boolean" },
      {
        field: "verifiable",
        headerName: "Verifiable",
        width: 90,
        type: "boolean",
      },
      { field: "capacity", headerName: "Capacity", width: 90 },
      {
        field: "actions",
        type: "actions",
        align: "right",
        getActions: ({ row }) => [
          <Tooltip title="Edit Portal" key="edit">
            <GridActionsCellItem
              label="Edit Portal"
              onClick={() => pushIdToUrl(row.id)}
              icon={<Edit />}
            />
          </Tooltip>,
        ],
      },
    ],
    [pushIdToUrl]
  );

  return (
    <Box sx={{ pt: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="portals"
        isURLDriven
        columns={columns}
        dataQuery={PORTAL_SEARCH}
        getItems={(data: PortalSearchQuery) => data.portalSearch.items ?? []}
        getCount={(data: PortalSearchQuery) => data.portalSearch.count ?? 0}
        noDataMessage="No portals found."
        initialPageSize={initialPageSize ?? 25}
        onRowClick={({ row }) => pushIdToUrl(row.id)}
        commandButtons={[
          <Button
            key="add-portal-button"
            variant="contained"
            onClick={() => setAddDialogOpen(true)}
          >
            Add Portal
          </Button>,
        ]}
      />
      {editPortalId && (
        <CreateEditPortalDialog
          onClose={popIdFromUrl}
          mode="edit"
          portalId={editPortalId}
        />
      )}
      {addDialogOpen && (
        <CreateEditPortalDialog
          onClose={() => setAddDialogOpen(false)}
          mode="add"
        />
      )}
    </Box>
  );
};
